import { useLingui } from "@lingui/react/macro";
import type {
  ConversationAccess,
  ConversationIdentity,
  PendingConversation,
} from "@rakazo/chat-ui/workspace-result";
import {
  CONVERSATION_UNAVAILABLE,
  ConversationIdentitySchema,
} from "@rakazo/chat-ui/workspace-result";
import type { Artifact, Bot, Me } from "@rakazo/contracts";
import { fileChatDestination, TICKET_UNAVAILABLE, ticketChatDestination } from "@rakazo/contracts";
import {
  AvatarStyleProvider,
  BotAvatar,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
} from "@rakazo/ui-web";
import { FileText, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appMarkUrl } from "../lib/branding";
import { createSpaceRpc, selectedSpaceId, selectSpace } from "../lib/rpc";
import { errorText } from "../lib/user-error";

export function ChatRecipientDialog({
  title,
  onSelect,
  onClose,
}: {
  title: string;
  onSelect: (
    bot: Bot,
    spaceId: string | null,
    isCurrent: () => boolean,
  ) =>
    | { spaceId: string; webPath: string }
    | null
    | Promise<{ spaceId: string; webPath: string } | null>;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const selection = useRef(0);
  const close = () => {
    selection.current++;
    onClose();
  };
  useEffect(
    () => () => {
      selection.current++;
    },
    [],
  );
  const [rpc] = useState(() => createSpaceRpc(selectedSpaceId()));
  const [bots, setBots] = useState<Bot[]>([]);
  const [profile, setProfile] = useState<Me | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let live = true;
    setBusy(true);
    setError("");
    void Promise.all([rpc.bots.list(), rpc.me(), rpc.echo.get()])
      .then(([bots, me, echo]) => {
        if (live) {
          setBots(echo ? [echo, ...bots.filter((bot) => bot.id !== echo.id)] : bots);
          setProfile(me);
        }
      })
      .catch((e) => {
        if (live) setError(errorText(e));
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
    };
  }, [rpc, revision]);
  const matches = bots.filter((bot) => bot.name.toLowerCase().includes(query.toLowerCase()));
  return (
    <AvatarStyleProvider value={profile?.avatarStyle ?? "organic"} brandMarkUrl={appMarkUrl}>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <DialogContent className="max-w-sm gap-3">
          <DialogTitle>{title}</DialogTitle>
          <Input
            aria-label={t`Find a bot`}
            placeholder={t`Find a bot…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
              <Button variant="ghost" onClick={() => setRevision((n) => n + 1)}>{t`Retry`}</Button>
            </p>
          )}
          <div className="max-h-[50vh] overflow-auto">
            {busy && (
              <p role="status" className="p-3 text-sm text-muted-foreground">{t`Loading…`}</p>
            )}
            {!busy && !error && !matches.length && (
              <p className="p-3 text-sm text-muted-foreground">{t`No results`}</p>
            )}
            {matches.map((bot) => (
              <button
                key={bot.id}
                type="button"
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm hover:bg-muted/50"
                onClick={async () => {
                  const attempt = ++selection.current;
                  const isCurrent = () => attempt === selection.current;
                  setError("");
                  try {
                    const target = await onSelect(bot, profile?.spaceId ?? null, isCurrent);
                    if (!isCurrent()) return;
                    if (!target) {
                      setError(TICKET_UNAVAILABLE);
                      return;
                    }
                    if (!selectSpace(target.spaceId)) {
                      setError(t`Could not switch space`);
                      return;
                    }
                    close();
                    navigate(target.webPath);
                  } catch {
                    if (isCurrent()) setError(CONVERSATION_UNAVAILABLE);
                  }
                }}
              >
                <BotAvatar identity={bot.id} color={bot.color} size={30} />
                <span className="truncate">{bot.name}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AvatarStyleProvider>
  );
}

export function ChatAboutFile({
  artifactId,
  onClose,
}: {
  artifactId: string;
  onClose: () => void;
}) {
  const { t } = useLingui();
  return (
    <ChatRecipientDialog
      title={t`Chat about this file`}
      onClose={onClose}
      onSelect={(bot, spaceId) => fileChatDestination(artifactId, spaceId, bot)}
    />
  );
}

export function ChatAboutTicket({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const { t } = useLingui();
  const sourceSpaceId = selectedSpaceId();
  return (
    <ChatRecipientDialog
      title={t`Chat about this ticket`}
      onClose={onClose}
      onSelect={(bot) =>
        sourceSpaceId ? ticketChatDestination(ticketId, sourceSpaceId, bot) : null
      }
    />
  );
}

/** Selects an immutable saved version; the recipient's composer stages it without sending. */
export function FilePicker({
  onPick,
  onClose,
}: {
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [rpc] = useState(() => createSpaceRpc(selectedSpaceId()));
  const [query, setQuery] = useState("");
  const [favorites, setFavorites] = useState(false);
  const [items, setItems] = useState<Artifact[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    let live = true;
    setBusy(true);
    setError("");
    setItems([]);
    setCursor(null);
    const timer = setTimeout(() => {
      void rpc.artifacts
        .listSpace({ library: { view: favorites ? "favorites" : "all", query }, limit: 60 })
        .then((page) => {
          if (live) {
            setItems(page.items);
            setCursor(page.nextCursor);
          }
        })
        .catch((e) => {
          if (live) setError(errorText(e));
        })
        .finally(() => {
          if (live) setBusy(false);
        });
    }, 150);
    return () => {
      live = false;
      if (generation.current === current) generation.current++;
      clearTimeout(timer);
    };
  }, [query, favorites, rpc, revision]);
  async function more() {
    if (!cursor || busy) return;
    const current = generation.current;
    setBusy(true);
    try {
      const page = await rpc.artifacts.listSpace({
        library: { view: favorites ? "favorites" : "all", query },
        cursor,
        limit: 60,
      });
      if (current === generation.current) {
        setItems((items) => [...items, ...page.items]);
        setCursor(page.nextCursor);
      }
    } catch (e) {
      if (current === generation.current) setError(errorText(e));
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }
  async function pick(file: Artifact) {
    if (busy) return;
    const current = generation.current;
    setBusy(true);
    setError("");
    try {
      // Library rows use the stable family ID. Resolve the displayed version,
      // rather than attaching the root or a newer version created during picking.
      const versions = await rpc.artifacts.listVersions({ familyId: file.id });
      const version = versions.find((entry) => entry.version === file.version);
      if (!version) throw new Error(t`Could not load this artifact.`);
      if (current === generation.current) onPick(version.id);
    } catch (e) {
      if (current === generation.current) setError(errorText(e));
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-md gap-3">
        <DialogTitle>{t`Files`}</DialogTitle>
        <div className="flex gap-2">
          <Input
            aria-label={t`Search files`}
            placeholder={t`Search files…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label={t`Favorites`}
            aria-pressed={favorites}
            onClick={() => setFavorites(!favorites)}
          >
            <Star size={17} fill={favorites ? "currentColor" : "none"} />
          </Button>
        </div>
        <div className="max-h-[50vh] overflow-auto">
          {items.map((file) => (
            <button
              type="button"
              key={file.id}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-muted/50"
              disabled={busy}
              onClick={() => void pick(file)}
            >
              <FileText size={19} className="shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">v{file.version}</span>
            </button>
          ))}
          {busy && <p role="status" className="p-3 text-sm text-muted-foreground">{t`Loading…`}</p>}
          {!busy && !items.length && !error && (
            <p className="p-3 text-sm text-muted-foreground">{t`No files found`}</p>
          )}
          {error && (
            <div role="alert" className="p-2 text-sm text-destructive">
              {error}{" "}
              <Button variant="ghost" onClick={() => setRevision((n) => n + 1)}>{t`Retry`}</Button>
            </div>
          )}
          {cursor && !error && (
            <Button
              variant="ghost"
              className="w-full"
              disabled={busy}
              onClick={() => void more()}
            >{t`Load more`}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Authorization precedes the functional merge into the recipient's current recovered draft. */
export function ConversationToChat({
  source,
  authorize,
  onStage,
  onClose,
}: {
  source: ConversationIdentity;
  authorize: ConversationAccess;
  onStage: (reference: PendingConversation) => void;
  onClose: () => void;
}) {
  const { t } = useLingui();
  return (
    <ChatRecipientDialog
      title={t`Reference conversation`}
      onClose={onClose}
      onSelect={async (bot, spaceId, isCurrent) => {
        const identity = ConversationIdentitySchema.parse(source);
        const targetSpace = bot.spaceId ?? spaceId;
        if (!targetSpace || targetSpace !== selectedSpaceId())
          throw new Error(CONVERSATION_UNAVAILABLE);
        try {
          await authorize({
            source: identity,
            recipient: { id: bot.id, spaceId: targetSpace },
            operation: "stage",
          });
        } catch {
          throw new Error(CONVERSATION_UNAVAILABLE);
        }
        if (!isCurrent()) return null;
        onStage({ id: crypto.randomUUID(), threadKey: bot.id, source: identity });
        return { spaceId: targetSpace, webPath: `/app/${encodeURIComponent(bot.id)}` };
      }}
    />
  );
}
